import { useState, useEffect, useCallback } from "react";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { clientPortalApi } from "@/services/api";
import SiteVisitForm from "./components/SiteVisitForm";

// ── Types ────────────────────────────────────────────────────────────────────
interface PortalDeliverable {
  id: number;
  name: string;
  type: string;
  size: string;
  available: boolean;
  uploadedOn: string | null;
  status: "Delivered" | "Approved" | "Under Review" | "Pending";
}

interface TimelineEvent {
  status: string;
  date: string;
  note: string;
  done: boolean;
  current?: boolean;
}

interface PortalInvoice {
  id: number;
  invoiceNo: string;
  type: "Invoice" | "Quotation" | "Proforma";
  amount: number;
  tax: number;
  total: number;
  status: "Paid" | "Pending" | "Overdue" | "Approved";
  dueDate: string;
  paidDate: string | null;
  jobNo: string;
}

interface PortalJob {
  jobNo: string;
  title: string;
  status: string;
  progress: number;
  assignedDate: string;
  dueDate: string;
  services: string[];
  location: string;
  deliverables: PortalDeliverable[];
  timeline: TimelineEvent[];
  invoices: PortalInvoice[];
}

// ── Mock fallback data ────────────────────────────────────────────────────────
const mockPortalJobs: PortalJob[] = [
  {
    jobNo: "HYD-SURV-2026-04-0034",
    title: "Topographic Survey — Shamshabad Industrial Zone",
    status: "In Progress",
    progress: 45,
    assignedDate: "2026-04-01",
    dueDate: "2026-04-20",
    services: ["Topographic Survey", "CAD Drafting"],
    location: "Shamshabad, Hyderabad",
    deliverables: [
      { id: 1, name: "Topographic Survey Report (PDF)", type: "pdf", size: "—", available: false, uploadedOn: null, status: "Pending" },
      { id: 2, name: "AutoCAD Drawing (DWG)", type: "dwg", size: "—", available: false, uploadedOn: null, status: "Pending" },
      { id: 3, name: "KMZ / GIS Data File", type: "kmz", size: "—", available: false, uploadedOn: null, status: "Pending" },
      { id: 4, name: "Field Photographs (ZIP)", type: "zip", size: "45.2 MB", available: true, uploadedOn: "2026-04-05", status: "Approved" },
    ],
    timeline: [
      { status: "Job Created", date: "2026-04-01", note: "Job card created and team assigned", done: true },
      { status: "Field Survey Started", date: "2026-04-06", note: "Field team checked in at Shamshabad site", done: true, current: true },
      { status: "Field Survey Completed", date: "", note: "Estimated: April 12", done: false },
      { status: "CAD Processing", date: "", note: "Estimated: April 15", done: false },
      { status: "Quality Check & Review", date: "", note: "Estimated: April 18", done: false },
      { status: "Deliverables Ready", date: "", note: "Estimated: April 20", done: false },
    ],
    invoices: [
      { id: 1, invoiceNo: "QT-2026-0045", type: "Quotation", amount: 125000, tax: 22500, total: 147500, status: "Approved", dueDate: "2026-04-25", paidDate: null, jobNo: "HYD-SURV-2026-04-0034" },
    ],
  },
  {
    jobNo: "HYD-SURV-2026-03-0028",
    title: "As-Built Survey — Kukatpally Metro Station",
    status: "Completed",
    progress: 100,
    assignedDate: "2026-03-10",
    dueDate: "2026-03-30",
    services: ["As-Built Survey", "CAD Drafting"],
    location: "Kukatpally, Hyderabad",
    deliverables: [
      { id: 5, name: "As-Built Survey Report (PDF)", type: "pdf", size: "4.2 MB", available: true, uploadedOn: "2026-03-30", status: "Delivered" },
      { id: 6, name: "AutoCAD Drawing v3.0 (DWG)", type: "dwg", size: "18.5 MB", available: true, uploadedOn: "2026-03-29", status: "Delivered" },
      { id: 7, name: "Excel Data Sheet (XLSX)", type: "xlsx", size: "1.4 MB", available: true, uploadedOn: "2026-03-30", status: "Delivered" },
      { id: 8, name: "Site Photographs (ZIP)", type: "zip", size: "32.1 MB", available: true, uploadedOn: "2026-03-28", status: "Delivered" },
    ],
    timeline: [
      { status: "Job Created", date: "2026-03-10", note: "Job card created and team assigned", done: true },
      { status: "Field Survey Started", date: "2026-03-12", note: "Field team at Kukatpally Metro site", done: true },
      { status: "Field Survey Completed", date: "2026-03-22", note: "All measurements recorded successfully", done: true },
      { status: "CAD Processing", date: "2026-03-25", note: "Drawings prepared by Amit Patel", done: true },
      { status: "Quality Check & Review", date: "2026-03-28", note: "Reviewed and approved by Priya Sharma", done: true },
      { status: "Deliverables Ready", date: "2026-03-30", note: "All files delivered to client", done: true },
    ],
    invoices: [
      { id: 2, invoiceNo: "INV-2026-0089", type: "Invoice", amount: 85000, tax: 15300, total: 100300, status: "Paid", dueDate: "2026-04-05", paidDate: "2026-04-04", jobNo: "HYD-SURV-2026-03-0028" },
    ],
  },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatTimeAgo(dateStr: string): string {
  const now = new Date("2026-04-07T12:00:00");
  const then = new Date(dateStr);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  return `${diffDays}d ago`;
}

const jobStatusConfig: Record<string, { bg: string; text: string; dot: string }> = {
  "In Progress": { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  "Completed":   { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  "Delivered":   { bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400" },
  "Pending":     { bg: "bg-gray-100", text: "text-gray-500", dot: "bg-gray-300" },
};

const invoiceStatusConfig: Record<string, { bg: string; text: string }> = {
  "Paid":     { bg: "bg-amber-100", text: "text-amber-700" },
  "Pending":  { bg: "bg-amber-100",   text: "text-amber-700" },
  "Overdue":  { bg: "bg-red-100",     text: "text-red-600" },
  "Approved": { bg: "bg-violet-100",  text: "text-violet-700" },
};

const deliverableStatusConfig: Record<string, { bg: string; text: string; icon: string }> = {
  "Delivered":    { bg: "bg-amber-100", text: "text-amber-700", icon: "ri-checkbox-circle-line" },
  "Approved":     { bg: "bg-teal-100",    text: "text-teal-700",    icon: "ri-check-line" },
  "Under Review": { bg: "bg-amber-100",   text: "text-amber-700",   icon: "ri-eye-line" },
  "Pending":      { bg: "bg-gray-100",    text: "text-gray-500",    icon: "ri-time-line" },
};

const fileTypeConfig: Record<string, { icon: string; color: string; bg: string }> = {
  pdf:  { icon: "ri-file-pdf-line",   color: "#ef4444", bg: "#fef2f2" },
  dwg:  { icon: "ri-file-code-line",  color: "#f59e0b", bg: "#fffbeb" },
  kmz:  { icon: "ri-map-2-line",      color: "#10b981", bg: "#ecfdf5" },
  xlsx: { icon: "ri-file-excel-line", color: "#059669", bg: "#d1fae5" },
  zip:  { icon: "ri-folder-zip-line", color: "#64748b", bg: "#f8fafc" },
};

// ── Portal Notification Types ─────────────────────────────────────────────────
interface PortalNotification {
  id: number;
  type: "status" | "deliverable" | "invoice" | "deadline" | "message";
  title: string;
  message: string;
  time: string;
  read: boolean;
  jobNo?: string;
  icon: string;
  iconBg: string;
  iconColor: string;
}

// ── Mock portal notifications ─────────────────────────────────────────────────
const mockPortalNotifications: PortalNotification[] = [
  {
    id: 1,
    type: "status",
    title: "Field Survey Started",
    message: "The field team has checked in at Shamshabad site and begun topographic measurements. Estimated completion: April 12.",
    time: "2026-04-06T08:30:00",
    read: false,
    jobNo: "HYD-SURV-2026-04-0034",
    icon: "ri-map-pin-user-line",
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
  },
  {
    id: 2,
    type: "deliverable",
    title: "File Ready for Download",
    message: "Field Photographs (ZIP, 45.2 MB) for your Shamshabad project have been uploaded and are ready to download.",
    time: "2026-04-05T17:00:00",
    read: false,
    jobNo: "HYD-SURV-2026-04-0034",
    icon: "ri-folder-check-line",
    iconBg: "bg-teal-50",
    iconColor: "text-teal-600",
  },
  {
    id: 3,
    type: "invoice",
    title: "Quotation Approved",
    message: "Quotation QT-2026-0045 (₹1,47,500) for your Shamshabad project has been approved. Work is now in progress.",
    time: "2026-04-04T14:00:00",
    read: false,
    jobNo: "HYD-SURV-2026-04-0034",
    icon: "ri-bill-line",
    iconBg: "bg-violet-50",
    iconColor: "text-violet-600",
  },
  {
    id: 4,
    type: "status",
    title: "Job Card Created",
    message: "Your project HYD-SURV-2026-04-0034 (Topographic Survey — Shamshabad Industrial Zone) has been created and team assigned.",
    time: "2026-04-01T10:00:00",
    read: true,
    jobNo: "HYD-SURV-2026-04-0034",
    icon: "ri-file-add-line",
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
  },
  {
    id: 5,
    type: "status",
    title: "Project Completed",
    message: "Your Kukatpally Metro Station As-Built Survey (HYD-SURV-2026-03-0028) has been completed. All deliverables are ready.",
    time: "2026-03-30T16:00:00",
    read: true,
    jobNo: "HYD-SURV-2026-03-0028",
    icon: "ri-checkbox-circle-line",
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
  },
  {
    id: 6,
    type: "deliverable",
    title: "All Files Delivered",
    message: "As-Built Survey Report, AutoCAD Drawing v3.0, Excel Data Sheet, and Site Photographs have all been delivered for your Kukatpally project.",
    time: "2026-03-30T15:30:00",
    read: true,
    jobNo: "HYD-SURV-2026-03-0028",
    icon: "ri-download-cloud-line",
    iconBg: "bg-teal-50",
    iconColor: "text-teal-600",
  },
  {
    id: 7,
    type: "invoice",
    title: "Payment Confirmed",
    message: "Payment of ₹1,00,300 for Invoice INV-2026-0089 has been received and confirmed. Thank you!",
    time: "2026-04-04T11:30:00",
    read: true,
    jobNo: "HYD-SURV-2026-03-0028",
    icon: "ri-money-rupee-circle-line",
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
  },
  {
    id: 8,
    type: "deadline",
    title: "Upcoming Delivery Date",
    message: "Your Shamshabad Topographic Survey is due on April 20. The team is currently at 45% completion and on track.",
    time: "2026-04-04T09:00:00",
    read: true,
    jobNo: "HYD-SURV-2026-04-0034",
    icon: "ri-calendar-event-line",
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
  },
];

// ── Login Screen ─────────────────────────────────────────────────────────────
interface LoginScreenProps {
  companyName: string;
  logo: string;
  email: string;
  onLogin: (token: string, clientName: string, clientCompany: string) => void;
}

function LoginScreen({ companyName, logo, email, onLogin }: LoginScreenProps) {
  const [form, setForm] = useState({ code: "", email: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await clientPortalApi.login(form.code, form.email);
      if (res?.token) {
        onLogin(res.token, res.clientName || "Client", res.clientCompany || "");
      } else {
        throw new Error("Invalid response");
      }
    } catch {
      setError("Invalid access code or email. Please contact your project manager.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdfa 100%)" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-white border border-amber-100 flex items-center justify-center mx-auto mb-4">
            <img src={logo} alt="Logo" className="w-10 h-10 object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{companyName}</h1>
          <p className="text-gray-500 text-sm mt-1">Client Project Portal</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-8">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Access Your Projects</h2>
          <p className="text-sm text-gray-500 mb-6 leading-relaxed">
            Enter your client access code and registered email to view job status, invoices, and download deliverables.
          </p>

          {error && (
            <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <span className="w-4 h-4 flex items-center justify-center text-red-500 mt-0.5 flex-shrink-0"><i className="ri-error-warning-line" /></span>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Client Access Code</label>
              <input
                type="text"
                required
                placeholder="e.g. HMDA2026"
                value={form.code}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Registered Email</label>
              <input
                type="email"
                name="email"
                required
                placeholder="your@email.com"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-amber-600 text-white rounded-xl font-semibold text-sm hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {loading ? <><i className="ri-loader-4-line animate-spin mr-2" />Verifying...</> : "Access Portal"}
            </button>
          </form>


        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Need help? Contact us at{" "}
          <a href={`mailto:${email}`} className="text-amber-600 hover:underline">{email}</a>
        </p>
      </div>
    </div>
  );
}

// ── Notifications Tab Component ───────────────────────────────────────────────
interface NotificationsTabProps {
  notifications: PortalNotification[];
  onMarkRead: (id: number) => void;
  onMarkAllRead: () => void;
}

function NotificationsTab({ notifications, onMarkRead, onMarkAllRead }: NotificationsTabProps) {
  const unread = notifications.filter((n) => !n.read).length;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-gray-800">Project Notifications</h3>
            {unread > 0 && (
              <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full font-bold">{unread} new</span>
            )}
          </div>
          {unread > 0 && (
            <button
              onClick={onMarkAllRead}
              className="text-xs text-amber-600 hover:text-amber-700 font-semibold cursor-pointer whitespace-nowrap"
            >
              Mark all as read
            </button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-notification-off-line text-xl text-gray-400" />
            </div>
            <p className="text-sm text-gray-500 font-medium">No notifications yet</p>
            <p className="text-xs text-gray-400 mt-1">Updates about your projects will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {notifications.map((notif) => (
              <div
                key={notif.id}
                onClick={() => onMarkRead(notif.id)}
                className={`flex items-start gap-4 px-5 py-4 cursor-pointer transition-all hover:bg-gray-50 ${!notif.read ? "bg-amber-50/30" : ""}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${notif.iconBg}`}>
                  <i className={`${notif.icon} text-lg ${notif.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm font-semibold ${!notif.read ? "text-gray-900" : "text-gray-700"}`}>
                      {notif.title}
                      {!notif.read && <span className="ml-2 inline-block w-2 h-2 bg-amber-500 rounded-full align-middle" />}
                    </p>
                    <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">{formatTimeAgo(notif.time)}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{notif.message}</p>
                  {notif.jobNo && (
                    <span className="inline-block mt-1.5 text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                      {notif.jobNo}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification preferences info */}
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
            <i className="ri-mail-send-line text-amber-600" />
          </span>
          <div>
            <p className="text-sm font-semibold text-amber-800">Email Notifications Active</p>
            <p className="text-xs text-amber-700 mt-1 leading-relaxed">
              You&apos;ll receive email updates for status changes, new deliverables, and invoice activity.
              Contact your project manager to update notification preferences.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Portal Dashboard ──────────────────────────────────────────────────────────
type PortalTab = "status" | "deliverables" | "invoices" | "timeline" | "notifications" | "visit";

export default function ClientPortalPage() {
  const { settings } = useCompanySettings();
  const [loggedIn, setLoggedIn] = useState(false);
  const [portalToken, setPortalToken] = useState("");
  const [clientName, setClientName] = useState("Venkat Rao");
  const [clientCompany, setClientCompany] = useState("HMDA Projects Ltd.");
  const [portalJobs, setPortalJobs] = useState<PortalJob[]>(mockPortalJobs);
  const [selectedJob, setSelectedJob] = useState<PortalJob>(mockPortalJobs[0]);
  const [activeTab, setActiveTab] = useState<PortalTab>("status");
  const [downloadedIds, setDownloadedIds] = useState<Set<number>>(new Set());
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [portalNotifications, setPortalNotifications] = useState<PortalNotification[]>(mockPortalNotifications);

  const loadPortalJobs = useCallback(async (token: string) => {
    setLoadingJobs(true);
    try {
      const res = await clientPortalApi.getJobs(token);
      if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
        setPortalJobs(res.data);
        setSelectedJob(res.data[0]);
      }
    } catch {
      // API offline — keep mock data
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    if (loggedIn && portalToken) {
      loadPortalJobs(portalToken);
    }
  }, [loggedIn, portalToken, loadPortalJobs]);

  const handleLogin = (token: string, name: string, company: string) => {
    setPortalToken(token);
    setClientName(name);
    setClientCompany(company);
    setLoggedIn(true);
  };

  const handleDownload = (id: number) => {
    setDownloadedIds((prev) => new Set([...prev, id]));
  };

  const unreadCount = portalNotifications.filter((n) => !n.read).length;

  const handleMarkRead = (id: number) => {
    setPortalNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
  };

  const handleMarkAllRead = () => {
    setPortalNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  if (!loggedIn) {
    return (
      <LoginScreen
        companyName={settings.name}
        logo={settings.logo}
        email={settings.email}
        onLogin={handleLogin}
      />
    );
  }

  const totalInvoiced = selectedJob.invoices.reduce((a, b) => a + b.total, 0);
  const totalPaid = selectedJob.invoices.filter((i) => i.status === "Paid").reduce((a, b) => a + b.total, 0);
  const deliverablesDone = selectedJob.deliverables.filter((d) => d.available).length;
  const pendingApprovalInvoices = selectedJob.invoices.filter((i) => i.status === "Pending" || i.status === "Overdue").length;

  return (
    <div className="min-h-screen" style={{ background: "#f8fafc" }}>
      {/* Portal Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <img src={settings.logo} alt="Logo" className="w-6 h-6 object-contain" />
            </div>
            <div>
              <p className="font-bold text-sm text-gray-900">{settings.name}</p>
              <p className="text-xs text-gray-400">Client Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Notification bell */}
            <button
              onClick={() => setActiveTab("notifications")}
              className="relative w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-notification-3-line text-gray-600 text-lg" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold leading-none">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
              <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold">
                {clientName.split(" ").map((n) => n[0]).join("").slice(0, 2)}
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-xs">{clientName}</p>
                <p className="text-xs text-gray-400">{clientCompany}</p>
              </div>
            </div>
            <button
              onClick={() => { setLoggedIn(false); setPortalToken(""); setPortalJobs(mockPortalJobs); setSelectedJob(mockPortalJobs[0]); }}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap px-3 py-1.5 rounded-lg hover:bg-red-50"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-logout-box-r-line" /></span>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Your Projects</h1>
            <p className="text-sm text-gray-500 mt-0.5">Track job progress, view invoices, and download completed deliverables</p>
          </div>
          <div className="flex items-center gap-3">
            {loadingJobs && (
              <span className="text-xs text-amber-500 flex items-center gap-1.5">
                <i className="ri-loader-4-line animate-spin" />Loading live data...
              </span>
            )}
            <button
              onClick={() => setActiveTab("visit")}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-semibold cursor-pointer hover:bg-teal-700 transition-all whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-2-line text-sm" /></span>
              Request Site Visit
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-5">
          {/* ── Job List sidebar ── */}
          <div className="col-span-1 space-y-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">Your Jobs ({portalJobs.length})</p>
            {portalJobs.map((job) => {
              const sc = jobStatusConfig[job.status] || jobStatusConfig["Pending"];
              const isActive = selectedJob.jobNo === job.jobNo;
              return (
                <div
                  key={job.jobNo}
                  onClick={() => { setSelectedJob(job); setActiveTab("status"); }}
                  className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${isActive ? "border-amber-400 ring-1 ring-amber-200" : "border-gray-100 hover:border-gray-200"}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sc.dot}`} />
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sc.bg} ${sc.text}`}>{job.status}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-800 leading-snug">{job.title}</p>
                  <p className="text-xs text-gray-400 mt-1 font-mono">{job.jobNo}</p>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Progress</span>
                      <span className="font-bold text-gray-700">{job.progress}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${job.progress === 100 ? "bg-amber-500" : "bg-amber-400"}`}
                        style={{ width: `${job.progress}%` }}
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
                    <span>Due {formatDate(job.dueDate)}</span>
                    <span>{job.deliverables.filter((d) => d.available).length}/{job.deliverables.length} files</span>
                  </div>
                </div>
              );
            })}

            <div className="bg-amber-600 rounded-xl p-4 text-white">
              <p className="text-xs font-semibold text-amber-200 mb-3">Account Summary</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-amber-200">Total Jobs</span>
                  <span className="font-bold">{portalJobs.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-amber-200">Completed</span>
                  <span className="font-bold">{portalJobs.filter((j) => j.status === "Completed" || j.status === "Delivered").length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-amber-200">Total Billed</span>
                  <span className="font-bold">₹{(portalJobs.reduce((a, b) => a + b.invoices.reduce((x, y) => x + y.total, 0), 0) / 100000).toFixed(1)}L</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Job Detail ── */}
          <div className="col-span-3 space-y-4">
            {/* Job header card */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{selectedJob.title}</h2>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{selectedJob.jobNo}</span>
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <i className="ri-map-pin-line text-gray-400" />{selectedJob.location}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <i className="ri-calendar-line text-gray-400" />Due {formatDate(selectedJob.dueDate)}
                    </span>
                  </div>
                </div>
                <span className={`text-sm px-3 py-1 rounded-full font-semibold flex-shrink-0 ${jobStatusConfig[selectedJob.status]?.bg} ${jobStatusConfig[selectedJob.status]?.text}`}>
                  {selectedJob.status}
                </span>
              </div>

              <div className="grid grid-cols-4 gap-3 mb-4">
                {[
                  { label: "Progress", value: `${selectedJob.progress}%`, icon: "ri-bar-chart-line", color: "text-amber-600", bg: "bg-amber-50" },
                  { label: "Files Ready", value: `${deliverablesDone}/${selectedJob.deliverables.length}`, icon: "ri-folder-check-line", color: "text-teal-600", bg: "bg-teal-50" },
                  { label: "Total Billed", value: `₹${(totalInvoiced / 1000).toFixed(0)}K`, icon: "ri-bill-line", color: "text-violet-600", bg: "bg-violet-50" },
                  { label: "Amount Paid", value: `₹${(totalPaid / 1000).toFixed(0)}K`, icon: "ri-money-rupee-circle-line", color: "text-amber-600", bg: "bg-amber-50" },
                ].map((kpi) => (
                  <div key={kpi.label} className={`rounded-xl p-3 ${kpi.bg}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-4 h-4 flex items-center justify-center"><i className={`${kpi.icon} text-sm ${kpi.color}`} /></span>
                      <span className="text-xs text-gray-500">{kpi.label}</span>
                    </div>
                    <p className="text-lg font-bold text-gray-900">{kpi.value}</p>
                  </div>
                ))}
              </div>

              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                  <span className="font-medium">Overall Completion</span>
                  <span className="font-bold text-gray-800">{selectedJob.progress}%</span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${selectedJob.progress === 100 ? "bg-amber-500" : "bg-amber-400"}`}
                    style={{ width: `${selectedJob.progress}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
              {([
                { key: "status",        label: "Job Status",    icon: "ri-task-line" },
                { key: "deliverables",  label: "Deliverables",  icon: "ri-folder-3-line" },
                { key: "invoices",      label: "Invoices",      icon: "ri-bill-line" },
                { key: "timeline",      label: "Timeline",      icon: "ri-time-line" },
                { key: "notifications", label: "Updates",       icon: "ri-notification-3-line" },
                { key: "visit",         label: "Book Visit",    icon: "ri-map-pin-2-line" },
              ] as { key: PortalTab; label: string; icon: string }[]).map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === t.key ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className={`${t.icon} text-sm`} /></span>
                  {t.label}
                  {t.key === "invoices" && pendingApprovalInvoices > 0 && (
                    <span className="w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">{pendingApprovalInvoices}</span>
                  )}
                  {t.key === "notifications" && unreadCount > 0 && (
                    <span className="w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">{unreadCount}</span>
                  )}
                </button>
              ))}
            </div>

            {/* ── Status Tab ── */}
            {activeTab === "status" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center"><i className="ri-tools-line text-gray-500 text-sm" /></span>
                    Services Included
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.services.map((s) => (
                      <span key={s} className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-sm font-medium border border-amber-100">
                        <i className="ri-check-line mr-1" />{s}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5 pt-4 border-t border-gray-100">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Key Dates</h4>
                    <div className="space-y-2">
                      {[
                        { label: "Job Started", value: formatDate(selectedJob.assignedDate), icon: "ri-calendar-check-line", color: "text-amber-600" },
                        { label: "Due Date", value: formatDate(selectedJob.dueDate), icon: "ri-calendar-event-line", color: "text-amber-600" },
                      ].map((row) => (
                        <div key={row.label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                          <div className="flex items-center gap-2">
                            <span className="w-4 h-4 flex items-center justify-center"><i className={`${row.icon} text-sm ${row.color}`} /></span>
                            <span className="text-sm text-gray-600">{row.label}</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-800">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center"><i className="ri-list-check text-gray-500 text-sm" /></span>
                    Current Progress
                  </h3>
                  <div className="space-y-3">
                    {selectedJob.timeline.map((event, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${event.done ? "bg-amber-500" : event.current ? "bg-amber-400" : "bg-gray-100"}`}>
                          {event.done
                            ? <i className="ri-check-line text-white text-xs" />
                            : event.current
                            ? <i className="ri-loader-4-line text-white text-xs" />
                            : <span className="w-2 h-2 bg-gray-300 rounded-full" />}
                        </div>
                        <div className="flex-1 pb-3 border-b border-gray-50 last:border-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm font-semibold ${event.done ? "text-gray-900" : event.current ? "text-amber-700" : "text-gray-400"}`}>
                              {event.status}
                              {event.current && <span className="ml-2 text-xs bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full font-medium">In Progress</span>}
                            </p>
                            {event.date && <p className="text-xs text-gray-400 flex-shrink-0">{formatDate(event.date)}</p>}
                          </div>
                          <p className={`text-xs mt-0.5 ${event.done ? "text-gray-500" : event.current ? "text-amber-600" : "text-gray-300"}`}>{event.note}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Deliverables Tab ── */}
            {activeTab === "deliverables" && (
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800">Project Deliverables</h3>
                    <p className="text-xs text-gray-400 mt-0.5">{deliverablesDone} of {selectedJob.deliverables.length} files ready for download</p>
                  </div>
                  {deliverablesDone > 0 && (
                    <button className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white rounded-xl text-sm font-semibold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-download-2-line" /></span>
                      Download All ({deliverablesDone})
                    </button>
                  )}
                </div>

                <div className="divide-y divide-gray-50">
                  {selectedJob.deliverables.map((d) => {
                    const ftCfg = fileTypeConfig[d.type] || { icon: "ri-file-line", color: "#64748b", bg: "#f8fafc" };
                    const stCfg = deliverableStatusConfig[d.status] || deliverableStatusConfig["Pending"];
                    const isDownloaded = downloadedIds.has(d.id);
                    return (
                      <div key={d.id} className={`flex items-center gap-4 px-5 py-4 ${d.available ? "hover:bg-gray-50" : "opacity-60"} transition-all`}>
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: ftCfg.bg }}>
                          <i className={`${ftCfg.icon} text-xl`} style={{ color: ftCfg.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${d.available ? "text-gray-900" : "text-gray-400"}`}>{d.name}</p>
                          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                            {d.available
                              ? <><span className="text-xs text-gray-500">{d.size}</span><span className="text-gray-300">·</span><span className="text-xs text-gray-400">Uploaded {formatDate(d.uploadedOn)}</span></>
                              : <span className="text-xs text-gray-400">Not yet available — will be uploaded upon completion</span>}
                          </div>
                        </div>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold flex-shrink-0 flex items-center gap-1 ${stCfg.bg} ${stCfg.text}`}>
                          <i className={`${stCfg.icon} text-xs`} />{d.status}
                        </span>
                        {d.available ? (
                          <button
                            onClick={() => handleDownload(d.id)}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap flex-shrink-0 ${isDownloaded ? "bg-gray-100 text-gray-500" : "bg-amber-600 text-white hover:bg-amber-700"}`}
                          >
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className={isDownloaded ? "ri-check-line" : "ri-download-2-line"} />
                            </span>
                            {isDownloaded ? "Downloaded" : "Download"}
                          </button>
                        ) : (
                          <span className="px-4 py-2 bg-gray-100 text-gray-400 rounded-xl text-sm font-medium whitespace-nowrap flex-shrink-0">Pending</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {deliverablesDone === 0 && (
                  <div className="px-5 py-8 text-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-folder-3-line text-xl text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500 font-medium">No files ready yet</p>
                    <p className="text-xs text-gray-400 mt-1">Files will appear here once the team uploads them</p>
                  </div>
                )}
              </div>
            )}

            {/* ── Invoices Tab ── */}
            {activeTab === "invoices" && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: "Total Billed", value: `₹${totalInvoiced.toLocaleString()}`, icon: "ri-bill-line", color: "text-gray-700", bg: "bg-gray-50" },
                    { label: "Amount Paid", value: `₹${totalPaid.toLocaleString()}`, icon: "ri-checkbox-circle-line", color: "text-amber-600", bg: "bg-amber-50" },
                    { label: "Balance Due", value: `₹${(totalInvoiced - totalPaid).toLocaleString()}`, icon: "ri-time-line", color: totalInvoiced - totalPaid > 0 ? "text-amber-600" : "text-gray-400", bg: totalInvoiced - totalPaid > 0 ? "bg-amber-50" : "bg-gray-50" },
                  ].map((s) => (
                    <div key={s.label} className={`rounded-xl p-4 ${s.bg} flex items-center gap-3`}>
                      <span className="w-8 h-8 flex items-center justify-center"><i className={`${s.icon} text-xl ${s.color}`} /></span>
                      <div>
                        <p className="text-lg font-bold text-gray-900">{s.value}</p>
                        <p className="text-xs text-gray-500">{s.label}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-800">Invoice History</h3>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {selectedJob.invoices.map((inv) => {
                      const sc = invoiceStatusConfig[inv.status] || { bg: "bg-gray-100", text: "text-gray-500" };
                      return (
                        <div key={inv.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-all">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${inv.status === "Paid" ? "bg-amber-50" : "bg-gray-50"}`}>
                            <i className={`ri-bill-line text-lg ${inv.status === "Paid" ? "text-amber-600" : "text-gray-400"}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-bold text-gray-800">{inv.invoiceNo}</p>
                              <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{inv.type}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400 flex-wrap">
                              <span>Due {formatDate(inv.dueDate)}</span>
                              {inv.paidDate && <><span className="text-gray-200">·</span><span className="text-amber-600">Paid {formatDate(inv.paidDate)}</span></>}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-gray-900">₹{inv.total.toLocaleString()}</p>
                            <p className="text-xs text-gray-400">incl. GST ₹{inv.tax.toLocaleString()}</p>
                          </div>
                          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold flex-shrink-0 ${sc.bg} ${sc.text}`}>{inv.status}</span>
                          <button className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-xl text-xs font-medium cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap flex-shrink-0">
                            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-download-line text-xs" /></span>
                            Download PDF
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {selectedJob.invoices.length === 0 && (
                    <div className="px-5 py-8 text-center">
                      <p className="text-sm text-gray-400">No invoices yet for this job</p>
                    </div>
                  )}
                </div>

                <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                      <i className="ri-bank-line text-amber-600" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Payment Information</p>
                      <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                        For payment queries or to request a receipt, please contact us at{" "}
                        <a href={`mailto:${settings.email}`} className="font-semibold underline">{settings.email}</a>{" "}
                        or call <strong>{settings.phone}</strong>. Please quote your job number in all correspondence.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Timeline Tab ── */}
            {activeTab === "timeline" && (
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-800 mb-6">Project Timeline</h3>
                <div className="relative">
                  <div className="absolute left-[18px] top-4 bottom-4 w-0.5 bg-gray-100" />
                  <div className="space-y-6">
                    {selectedJob.timeline.map((event, i) => (
                      <div key={i} className="flex items-start gap-5 relative">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 z-10 border-2 ${
                          event.done ? "bg-amber-500 border-amber-500" : event.current ? "bg-amber-400 border-amber-400" : "bg-white border-gray-200"
                        }`}>
                          {event.done
                            ? <i className="ri-check-line text-white text-sm" />
                            : event.current
                            ? <i className="ri-loader-4-line text-white text-sm" />
                            : <span className="w-2.5 h-2.5 bg-gray-200 rounded-full" />}
                        </div>
                        <div className="flex-1 pt-1.5 pb-4 border-b border-gray-50 last:border-0">
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-bold ${event.done ? "text-gray-900" : event.current ? "text-amber-700" : "text-gray-400"}`}>
                                {event.status}
                              </p>
                              {event.current && <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full font-semibold">Active</span>}
                            </div>
                            {event.date && (
                              <span className="text-xs text-gray-400 bg-gray-50 px-2.5 py-1 rounded-full flex-shrink-0">{formatDate(event.date)}</span>
                            )}
                          </div>
                          <p className={`text-sm mt-1 leading-relaxed ${event.done ? "text-gray-600" : event.current ? "text-amber-600" : "text-gray-300"}`}>
                            {event.note}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Notifications Tab ── */}
            {activeTab === "notifications" && (
              <NotificationsTab
                notifications={portalNotifications}
                onMarkRead={handleMarkRead}
                onMarkAllRead={handleMarkAllRead}
              />
            )}

            {/* ── Book Visit Tab ── */}
            {activeTab === "visit" && (
              <SiteVisitForm
                clientName={clientName}
                clientCompany={clientCompany}
                jobNo={selectedJob.jobNo}
                jobTitle={selectedJob.title}
                companyEmail={settings.email}
                companyPhone={settings.phone}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
